/**
 * Helper Node Registration Modal
 * Allows members on their personal machines to sovereignly configure and submit a ParticipationProposal.
 */

import React, { useState } from 'react';
import type { ParticipationProposal } from '../../mesh/compute/capacity-lease-contracts';

interface HelperNodeRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  workerNodeId: string;
  deviceKeyId: string;
  displayName: string;
  onSubmitProposal: (proposal: ParticipationProposal) => void;
}

export const HelperNodeRegistrationModal: React.FC<HelperNodeRegistrationModalProps> = ({
  isOpen,
  onClose,
  workerNodeId,
  deviceKeyId,
  displayName,
  onSubmitProposal,
}) => {
  const [maxCpu, setMaxCpu] = useState(50);
  const [maxMemoryGb, setMaxMemoryGb] = useState(4);
  const [pauseOnBattery, setPauseOnBattery] = useState(20);
  const [pauseOnThermal, setPauseOnThermal] = useState(true);
  const [pauseOnInteractive, setPauseOnInteractive] = useState(true);
  const [taskClassExtraction, setTaskClassExtraction] = useState(true);
  const [taskClassEmbedding, setTaskClassEmbedding] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const allowedTaskClasses: string[] = [];
    if (taskClassExtraction) allowedTaskClasses.push('needle-extraction');
    if (taskClassEmbedding) allowedTaskClasses.push('rag-indexing');

    const proposal: ParticipationProposal = {
      proposalId: `prop-${Date.now()}-${workerNodeId}`,
      workerNodeId,
      deviceKeyId,
      displayName,
      hostnameHint: typeof window !== 'undefined' ? window.location.hostname : undefined,
      offeredRoles: ['task-worker'],
      resourceCaps: {
        maxCpuPercent: maxCpu,
        maxMemoryBytes: maxMemoryGb * 1024 * 1024 * 1024,
        maxDiskStorageBytes: 0,
        allowedBackends: ['metal', 'cpu'],
      },
      allowedTaskClasses,
      storageLeaseBytes: 0,
      pauseOnBatteryPercent: pauseOnBattery,
      pauseOnThermalPressure: pauseOnThermal,
      pauseOnInteractiveUsage: pauseOnInteractive,
      timestamp: new Date().toISOString(),
      signature: `sig-prop-${deviceKeyId}-${Date.now()}`,
    };

    onSubmitProposal(proposal);
    setIsSubmitting(false);
    onClose();
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(5, 8, 16, 0.85)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
      }}
    >
      <div
        style={{
          width: '520px',
          background: 'linear-gradient(145deg, #111827, #0b0f19)',
          border: '1px solid rgba(56, 189, 248, 0.3)',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
          color: '#f8fafc',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>⚡</span>
            <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Register Device as Mesh Compute Helper</h2>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '18px', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>

        <p style={{ fontSize: '12px', color: '#94a3b8', margin: '0 0 16px 0', lineHeight: 1.5 }}>
          Lease spare local compute capacity to the sovereign mesh. All tasks run as stateless, read-only inference shards.
          You retain full sovereignty and can pause or revoke this lease anytime.
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Resource Limit Sliders */}
          <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '14px', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600 }}>Maximum CPU Allocation</label>
              <span style={{ fontSize: '12px', color: '#38bdf8', fontWeight: 700 }}>{maxCpu}%</span>
            </div>
            <input
              type="range"
              min="10"
              max="90"
              step="5"
              value={maxCpu}
              onChange={(e) => setMaxCpu(Number(e.target.value))}
              style={{ width: '100%', accentColor: '#38bdf8' }}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', marginBottom: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600 }}>Memory Cap (RAM)</label>
              <span style={{ fontSize: '12px', color: '#38bdf8', fontWeight: 700 }}>{maxMemoryGb} GB</span>
            </div>
            <input
              type="range"
              min="1"
              max="16"
              step="1"
              value={maxMemoryGb}
              onChange={(e) => setMaxMemoryGb(Number(e.target.value))}
              style={{ width: '100%', accentColor: '#38bdf8' }}
            />
          </div>

          {/* Sovereignty & Preemption Conditions */}
          <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '14px', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#a78bfa', marginBottom: '10px' }}>
              🛡️ Preemption & Battery Guardrails
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#cbd5e1', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={pauseOnInteractive}
                  onChange={(e) => setPauseOnInteractive(e.target.checked)}
                  style={{ accentColor: '#a78bfa' }}
                />
                Pause immediately when mouse/keyboard is in use (Instant Preemption)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#cbd5e1', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={pauseOnThermal}
                  onChange={(e) => setPauseOnThermal(e.target.checked)}
                  style={{ accentColor: '#a78bfa' }}
                />
                Pause automatically if hardware thermals throttle
              </label>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>Pause when battery drops below:</span>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#f59e0b' }}>{pauseOnBattery}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="50"
                step="5"
                value={pauseOnBattery}
                onChange={(e) => setPauseOnBattery(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#f59e0b' }}
              />
            </div>
          </div>

          {/* Allowed Task Classes */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <label style={{ flex: 1, padding: '8px 12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={taskClassExtraction}
                onChange={(e) => setTaskClassExtraction(e.target.checked)}
                style={{ accentColor: '#34d399' }}
              />
              Needle 2 Schema Extraction
            </label>
            <label style={{ flex: 1, padding: '8px 12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={taskClassEmbedding}
                onChange={(e) => setTaskClassEmbedding(e.target.checked)}
                style={{ accentColor: '#34d399' }}
              />
              RAG Embeddings
            </label>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                background: 'rgba(255, 255, 255, 0.05)',
                color: '#94a3b8',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                flex: 2,
                padding: '10px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #0284c7, #2563eb)',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              {isSubmitting ? 'Signing Proposal...' : '🚀 Submit Participation Proposal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
